// Search input
// Cancel button closes search input
// note there is no clear button
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) if (e.indexOf(p[i]) < 0)
            t[p[i]] = s[p[i]];
    return t;
};
// Flyover displays when search input receives focus
// Flyover contains the most common search terms by default
// When typing in the search input, do a partial match on product names and display in flyover
// On pressing enter (?) submit the search
import * as React from 'react';
import { callAllEventHandlers } from '../utils/callAllEventHandlers';
import { Query } from 'react-apollo';
import { searchQuery } from './searchQuery';
import debounce from 'lodash.debounce';
import { withRouter } from 'react-router-dom';
import get from 'lodash.get';
import logWarningInDev from '@jetshop/core/helpers/logWarningInDev';
class SearchFieldBehaviour extends React.Component {
    constructor() {
        super(...arguments);
        this.inputRef = React.createRef();
        this.flyoutRef = React.createRef();
        this.cleanup = () => { };
        this.initialState = {
            isDirty: false,
            term: this.props.initialValue,
            debouncedTerm: this.props.initialValue,
            hasFocus: false,
            isOpen: false
        };
        this.environment = typeof window === 'undefined' ? null : window;
        this.state = this.initialState;
        this.getInputProps = (_a = {}) => {
            var { onBlur, onFocus, refKey = 'ref' } = _a, rest = __rest(_a, ["onBlur", "onFocus", "refKey"]);
            const { updateSearch, handleBlur, handleFocus, handleEnterSubmit, inputRef } = this;
            const { term } = this.state;
            const { onSubmit } = this.props;
            // Only call custom submit handler on keydown if
            // Enter has been pressed
            let keyDownSubmit;
            if (onSubmit) {
                keyDownSubmit = (e) => this.handleEnterSubmit(e, onSubmit);
            }
            return Object.assign({ onChange: updateSearch, onBlur: callAllEventHandlers(onBlur, handleBlur), onFocus: callAllEventHandlers(onFocus, handleFocus), onKeyDown: callAllEventHandlers(keyDownSubmit, handleEnterSubmit), [refKey]: inputRef, autoComplete: 'off', value: term, type: 'search' }, rest
            // TODO: add an ID via props or generate one
            // TODO: add ARIA labels
            );
        };
        this.getFlyoutProps = (rest) => {
            const { flyoutRef } = this;
            return Object.assign({ ref: flyoutRef }, rest);
        };
        this.getCancelProps = (_a = {}) => {
            var { onClick } = _a, rest = __rest(_a, ["onClick"]);
            const { clearSearch } = this;
            const { onCancel } = this.props;
            return Object.assign({ onClick: callAllEventHandlers(onClick, onCancel, clearSearch) }, rest);
        };
        this.resetIfEmpty = (e) => {
            if (e.currentTarget.value === this.props.initialValue) {
                this.clearSearch();
            }
        };
        this.handleBlur = (e, cb) => {
            this.resetIfEmpty(e);
            this.updateFocus('blur');
            cb && typeof cb === 'function' && cb();
            return true;
        };
        this.handleEnterSubmit = (e, cb = this.triggerSearch) => {
            // First check that the key pressed was Enter, and then
            // call the onSubmit callback. We always call triggerSearch,
            // but the user has an opportunity to pass an onSubmit handler
            // as a prop
            const { term } = this.state;
            if (e.key === 'Enter') {
                cb(term);
            }
        };
        this.triggerSearch = (term) => {
            // Blur the input, close the search and navigate to the search page
            this.updateFocus('blur');
            this.setState({ isOpen: false });
            this.props.history.push(`/search/?term=${term}`);
        };
        // @ts-ignore event is not used
        this.handleFocus = (e, cb) => {
            this.setState({ isOpen: true });
            this.updateFocus('focus');
            cb && typeof cb === 'function' && cb();
            return true;
        };
        this.updateFocus = (focusState) => {
            if (!['blur', 'focus'].includes(focusState)) {
                throw new Error("updateFocus must be called with one of 'blur' | 'focus'");
            }
            const hasFocus = focusState === 'focus';
            // Trigger a blur or focus on the input
            hasFocus ? this.inputRef.current.focus() : this.inputRef.current.blur();
            // Update the state to reflect the current focus state
            this.setState({ hasFocus });
            return true;
        };
        this.debouncedUpdateTerm = debounce((term) => this.setState({ debouncedTerm: term }), 300);
        this.updateSearch = (e) => {
            const term = e.currentTarget.value;
            const isDirty = term !== this.props.initialValue;
            this.debouncedUpdateTerm(term);
            this.setState({
                term,
                isDirty
            });
        };
        this.clearSearch = () => {
            this.setState(this.initialState);
            this.updateFocus('blur');
        };
    }
    componentDidMount() {
        if (process.env.NODE_ENV === 'development') {
            console.warn('**********\n', 'DEPRECATION: The SearchFieldBehaviour component is deprecated. Please', 'use SearchAutoCompleteContainer instead. This component will be removed', 'in a later release.\n', '**********');
        }
        const targetIsInside = (target) => [this.flyoutRef, this.inputRef].some(node => node &&
            node.current &&
            (node.current === target || node.current.contains(target)));
        const onMouseUp = (event) => {
            if (!targetIsInside(event.target)) {
                this.handleBlur(event);
                this.setState({ isOpen: false });
            }
        };
        const onTouchStart = (event) => {
            if (!targetIsInside(event.target)) {
                this.handleBlur(event);
                this.setState({ isOpen: false });
            }
        };
        if (this.environment) {
            this.environment.addEventListener('mouseup', onMouseUp);
            this.environment.addEventListener('touchstart', onTouchStart);
        }
        if (this.props.focusOnLoad) {
            this.updateFocus('focus');
        }
        this.cleanup = () => {
            if (this.environment) {
                this.environment.removeEventListener('mouseup', onMouseUp);
                this.environment.removeEventListener('touchstart', onTouchStart);
            }
        };
    }
    componentWillUnmount() {
        this.cleanup();
    }
    render() {
        logWarningInDev(!this.props.searchQuery ||
            !!this.props.productsFragment ||
            this.props.includeFilters !== undefined, `SearchFieldBehaviour expects a searchQuery prop. Supplying 'resultFragment' and 'includeFilters' is deprecated and will be removed in the next major release.`);
        return (React.createElement(Query, { skip: !this.state.isDirty ||
                this.state.debouncedTerm === this.props.initialValue, variables: { term: this.state.debouncedTerm, limit: 5 }, query: this.props.searchQuery ||
                searchQuery({
                    resultFragment: this.props.productsFragment,
                    includeFilters: this.props.includeFilters || false
                }) }, ({ loading, data }) => {
            const totalResults = get(data, 'data.search.products.totalResults', null);
            const result = get(data, 'search.products.result', []);
            return this.props.children(Object.assign({ getInputProps: this.getInputProps, getCancelProps: this.getCancelProps, getFlyoutProps: this.getFlyoutProps, loading: this.state.isDirty ? loading : false, isOpen: this.state.isOpen, updateFocus: this.updateFocus, totalResults,
                result }, this.state));
        }));
    }
}
SearchFieldBehaviour.defaultProps = {
    initialValue: '',
    focusOnLoad: false
};
export default withRouter(SearchFieldBehaviour);
//# sourceMappingURL=SearchFieldBehaviour.js.map